import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import api from "../api/axios";

export default function PublicLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    if (newsletterSubmitting) return;

    const form = event.currentTarget;
    const email = new FormData(form).get("email");
    setNewsletterSubmitting(true);
    setNewsletterStatus(null);

    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      form.reset();
      setNewsletterStatus({ type: "success", text: data.message || "You're subscribed to MekPay updates." });
    } catch (error) {
      setNewsletterStatus({
        type: "error",
        text: error.response?.data?.message || "We couldn't complete your subscription. Please try again.",
      });
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const navLinks = [
    { name: "Home", to: "/" },
    { name: "About", to: "/about" },
    { name: "Contact", to: "/contact" },
    { name: "Security", to: "/security" },
    { name: "FAQs", to: "/faqs" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-800">
      <header className="sticky top-0 left-0 w-full bg-slate-950/95 backdrop-blur-md border-b border-white/10 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-white tracking-tight"
          >
            Mek<span className="text-cyan-300">Pay</span>
          </Link>

          <nav className="hidden md:flex items-center space-x-7 font-medium text-sm text-slate-200">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="hover:text-cyan-300 transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-3">
            <Link
              to="/login"
              className="text-slate-200 hover:text-cyan-300 transition-colors duration-200"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="rounded-xl bg-accent px-5 py-2 font-semibold text-slate-950 transition hover:opacity-90"
            >
              Open Account
            </Link>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-slate-950 border-t border-white/10 px-6 py-4 space-y-4 text-sm text-slate-200">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="block hover:text-cyan-300"
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-2 border-t border-white/10">
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="block py-2 hover:text-cyan-300"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                onClick={() => setMenuOpen(false)}
                className="block bg-accent text-slate-950 text-center py-2 rounded-xl font-semibold hover:opacity-90"
              >
                Open Account
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-slate-950 text-white">
        <div className="bg-[linear-gradient(120deg,rgba(15,23,42,1),rgba(30,64,175,0.95))] py-10 text-center px-6">
          <h3 className="text-2xl md:text-3xl font-semibold mb-3">
            Ready to move money with confidence?
          </h3>
          <p className="text-slate-200 mb-6 text-sm md:text-base">
            Join MekPay to manage transfers, bills, and digital assets from one secure dashboard.
          </p>
          <Link
            to="/register"
            className="inline-flex rounded-xl bg-accent px-6 py-3 font-semibold text-slate-950 hover:opacity-90 transition"
          >
            Create Account
          </Link>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 px-6 py-12">
          <div>
            <h2 className="text-2xl font-bold text-cyan-300 mb-3">MekPay</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              MekPay is a fintech and crypto platform built for secure payments, transfers, and reliable digital finance.
            </p>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Operates under MekBridge LTD. (RC No. 8923599), a fully registered Nigerian entity based in Lagos, Nigeria.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <Link to="/about" className="hover:text-cyan-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-cyan-300">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/security" className="hover:text-cyan-300">
                  Security
                </Link>
              </li>
              <li>
                <Link to="/faqs" className="hover:text-cyan-300">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-cyan-300">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-cyan-300">
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Products</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <Link to="/register" className="hover:text-cyan-300">
                  Wallet Account
                </Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-cyan-300">
                  Bill Payments
                </Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-cyan-300">
                  Bank Transfers
                </Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-cyan-300">
                  Crypto Access
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Stay Updated</h3>
            <p className="text-sm text-slate-300 mb-3">
              Subscribe for product updates, security notices, and feature releases.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
              <input
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                maxLength={254}
                className="w-full min-w-0 px-4 py-2 rounded-lg text-slate-900 focus:outline-none"
                required
              />
              <button
                type="submit"
                disabled={newsletterSubmitting}
                className="w-full bg-accent text-slate-950 px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition disabled:cursor-not-allowed disabled:opacity-60"
              >
                {newsletterSubmitting ? "Submitting..." : "Subscribe"}
              </button>
              {newsletterStatus && (
                <p role="status" aria-live="polite" className={`text-xs ${newsletterStatus.type === "success" ? "text-emerald-300" : "text-red-300"}`}>
                  {newsletterStatus.text}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 text-center py-4 text-sm text-slate-400">
          © {new Date().getFullYear()} MekPay. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
